import React, { memo, useCallback, useEffect } from "react";
import cx from "classnames";
import { css } from "@linaria/core";
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { escapeMarkdown, unescapeMarkdown } from "../../lib/markdown";
import { Bold, Italic, LinkM, LinkUnlinkM } from "../../IconV2/react";
import { colors, spacings } from "../../Theme";
import t from "../../i18n";
import { ToolbarButton } from "./ToolbarButton";
import useModal from "../../lib/hooks/useModal";
import ModalV2 from "../../ModalV2";
import ModalForm from "../../ModalV2/Form";
import InputV2 from "../../Input/InputV2";
import { getSelectedNode } from "../utils/getSelectedNode";
import { validateUrl } from "../../lib/urlTools";

function MarkdownToolbar_({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = React.useState(false);
  const [isItalic, setIsItalic] = React.useState(false);
  const [isLink, setIsLink] = React.useState(false);
  const [url, setUrl] = React.useState<string>();
  const [error, setError] = React.useState<string>();
  const { modal: linkEditorOpened, openModal, closeModal } = useModal();

  const closeLinkEditor = useCallback(() => {
    setUrl(undefined);
    closeModal();
  }, [closeModal]);

  const dispatchBold = useCallback(
    () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold"),
    [editor],
  );

  const dispatchItalic = useCallback(
    () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic"),
    [editor],
  );

  const dispatchLink = useCallback(() => {
    if (!url) return false;
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
      url: unescapeMarkdown(url),
      target: "_blank",
      rel: "noopener noreferrer",
    });
    return true;
  }, [editor, url]);

  const dispatchUnlink = useCallback(() => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
  }, [editor]);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node?.getParent();
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsLink($isLinkNode(parent) || $isLinkNode(node));
    } else {
      setIsBold(false);
      setIsItalic(false);
      setIsLink(false);
    }
  }, []);

  const updateUrl = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(escapeMarkdown(e.target.value));
    if (e.target.value.length === 0 || validateUrl(e.target.value)) {
      setError(undefined);
    } else {
      setError(t("markdown_editor.tool_bar.link.error.invalid_url"));
    }
  }, []);

  /** @note following effect are here to update toolbar states from editor selection changes */
  useEffect(() => {
    const unregister = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => updateToolbar());
    });
    return () => unregister();
  }, [editor, updateToolbar]);

  useEffect(() => {
    const unregister = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      1,
    );
    return () => unregister();
  }, [editor, updateToolbar]);

  /** @note Retrieve editor URL */
  useEffect(() => {
    if (linkEditorOpened) {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const node = selection.anchor.getNode();
          const parent = node.getParent();
          const linkNode = $isLinkNode(parent) ? parent : $isLinkNode(node) ? node : null;

          if (linkNode) {
            const linkUrl = linkNode.getURL();
            setUrl(linkUrl);
          } else {
            setUrl("");
          }
        }
      });
    }
  }, [editor, linkEditorOpened]);

  return (
    <div className={cx(className, toolbar)}>
      <ToolbarButton
        checked={isBold}
        onClick={dispatchBold}
        description={t("markdown_editor.tool_bar.tooltip.bold")}
      >
        <Bold />
      </ToolbarButton>
      <ToolbarButton
        checked={isItalic}
        onClick={dispatchItalic}
        description={t("markdown_editor.tool_bar.tooltip.italic")}
      >
        <Italic />
      </ToolbarButton>
      <ToolbarButton
        checked={isLink}
        onClick={openModal}
        description={
          isLink
            ? t("markdown_editor.tool_bar.link.replace")
            : t("markdown_editor.tool_bar.link.insert")
        }
      >
        <LinkM />
      </ToolbarButton>
      {isLink && (
        <ToolbarButton
          checked={isLink}
          onClick={dispatchUnlink}
          description={t("markdown_editor.tool_bar.link.remove")}
        >
          <LinkUnlinkM />
        </ToolbarButton>
      )}
      {children}
      <ModalV2
        title={t("markdown_editor.tool_bar.link.insert")}
        isOpen={linkEditorOpened}
        onRequestClose={closeLinkEditor}
        size="s"
      >
        <ModalForm
          onSubmit={url && url.length > 0 && !error ? dispatchLink : false}
          onRequestClose={closeLinkEditor}
          okButtonName={t("markdown_editor.modal.insert")}
        >
          <InputV2
            label={t("markdown_editor.modal.link.label")}
            placeholder={t("markdown_editor.modal.link.placeholder")}
            value={url}
            onChange={updateUrl}
            error={error}
            autoFocus
          />
        </ModalForm>
      </ModalV2>
    </div>
  );
}

export const MarkdownToolbar = memo(MarkdownToolbar_);

export function ToolbarSeparator() {
  return <div className={toolbarSeparator} />;
}

const toolbar = css`
  background-color: ${colors.titanium50};
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-bottom: 1px solid ${colors.titanium100};
  display: flex;
  flex-wrap: nowrap;
  gap: ${spacings.xs};
  padding: ${spacings.xxs} ${spacings.xs};
`;

const toolbarSeparator = css`
  background-color: ${colors.titanium200};
  width: 1px;
`;
